import React from 'react';
import { Box } from '@mui/material';
import { Typography } from '@mui/joy';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import moment from 'moment';
import { DescriptionRounded } from '@mui-symbols-material/w300';
import { AssetFiles } from '../../graphql/queries/assetFiles';
import { SystemFiles } from '../../graphql/queries/systemFiles';
import { HeroListCard, HeroListRow } from './heroPrimitives';
import { SkeletonListRows, fadeInSx } from './HeroSkeletons';

interface Props {
    variant: 'asset' | 'system';
    /** assetLocationId for the asset variant, systemId for the system variant. */
    id: string;
    compact?: boolean;
}

const formatSize = (bytes?: number | null): string => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface FileRow {
    id: string;
    filename: string;
    category?: string | null;
    insert?: string | null;
    size?: number | null;
    kind: string;
    link?: string | null;
}

/** Lists the most recent files for an asset (asset + product files) or a system. */
const FilesListWidget = ({ variant, id, compact }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const limit = compact ? 3 : 5;

    const { data, loading } = useQuery(variant === 'asset' ? AssetFiles : SystemFiles, {
        variables: { id },
        skip: !id,
    });

    let rows: FileRow[] = [];
    let total = 0;

    if (variant === 'asset') {
        const productFiles: any[] = _.get(data, 'assetLocation.asset.product.fileList.productNodeFiles', []) ?? [];
        const nodeFiles: any[] = _.get(data, 'assetLocation.installedBaseNode.fileList.installedBaseNodeFiles', []) ?? [];
        const productTotal = _.get(data, 'assetLocation.asset.product.fileList.total', productFiles.length) ?? 0;
        const nodeTotal = _.get(data, 'assetLocation.installedBaseNode.fileList.total', nodeFiles.length) ?? 0;
        total = productTotal + nodeTotal;
        rows = [
            ...nodeFiles.map((f) => ({ id: f.id, filename: f.filename, category: f.category, insert: f.insert, size: _.get(f, 'file.size'), kind: t('Asset file'), link: _.get(f, 'file.link') })),
            ...productFiles.map((f) => ({ id: f.id, filename: f.filename, category: f.category, insert: f.insert, size: _.get(f, 'file.size'), kind: t('Product file'), link: _.get(f, 'file.link') })),
        ];
    } else {
        const nodeFiles: any[] = _.get(data, 'system.installedBaseNode.fileList.installedBaseNodeFiles', []) ?? [];
        total = _.get(data, 'system.installedBaseNode.fileList.total', nodeFiles.length) ?? 0;
        rows = nodeFiles.map((f) => ({ id: f.id, filename: f.filename, category: f.category, insert: f.insert, size: _.get(f, 'file.size'), kind: t('System file'), link: _.get(f, 'file.link') }));
    }

    rows = _.orderBy(rows, [(r) => r.insert || ''], ['desc']).slice(0, limit);

    const goToFiles = () => navigate(variant === 'asset' ? `/assets/${id}/files` : `/systems/${id}/files`);
    const openFile = (link?: string | null) => {
        if (link) window.open(link, '_blank', 'noopener,noreferrer');
    };

    return (
        <HeroListCard
            title={t('Documents')}
            icon={<DescriptionRounded />}
            count={total}
            dense={compact}
            viewAll={total > 0 ? `${t('View all documents')} (${total})` : undefined}
            onViewAll={goToFiles}
        >
            {loading && !data ? (
                <SkeletonListRows rows={compact ? 2 : 3} />
            ) : total === 0 ? (
                <Typography level="bodySmall" sx={{ color: '#777' }}>
                    {t('No documents')}
                </Typography>
            ) : (
                <Box sx={fadeInSx}>
                    {rows.map((f) => (
                        <HeroListRow
                            key={f.id}
                            icon={<DescriptionRounded />}
                            title={f.filename}
                            meta={[f.kind, formatSize(f.size)].filter(Boolean).join(' · ') || undefined}
                            trailing={f.insert ? moment(f.insert).format('DD.MM.YYYY') : undefined}
                            onClick={() => openFile(f.link)}
                        />
                    ))}
                </Box>
            )}
        </HeroListCard>
    );
};

export default FilesListWidget;
